<template>
    <div class="options-menu">
        <div class="menu-title">
            <MenuBackButton @click="handleBackButtonClick" />
            <h5 class="label-uppercase-info">{{ menuLabel }}</h5>
        </div>

        <elm-text-field
            v-if="canSearch"
            v-elm-model="searchQuery"
            :placeholder="searchFieldPlaceholder"
            class="search-field"
            size="small"
        >
            <elm-actions-search-icon slot="icon-left" size="18"></elm-actions-search-icon>
        </elm-text-field>

        <DropdownItem
            withCheckbox
            title="Select all"
            class="select-all-option"
            :isSelected="areAllOptionsSelected"
            @click="handleSelectAll"
        ></DropdownItem>

        <div class="divider"></div>

        <ul class="options-list">
            <DropdownItem
                v-for="option of filteredOptions"
                :key="option.value"
                :title="option.label"
                withCheckbox
                :isSelected="selectedValues.includes(option.value)"
                @click="handleOptionClick(option)"
            ></DropdownItem>
        </ul>

        <div class="menu-footer">
            <elm-button size="small" color="primary" @click="handleConfirm">
                {{ trans('spa.admin.course-organization.filters-menu.confirm') }}
            </elm-button>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Emit, Mixins, Prop, Watch } from 'vue-property-decorator';
    import { TranslationMixin } from '@/translations/store';
    import { DropdownButton, DropdownItem } from '@/ui-kit';

    import '@eloomi/icons/actions/actions-search';
    import '@eloomi/components/text-field';
    import { FilterType, Option } from '@/common/components/filter/interfaces';
    import MenuBackButton from '@/common/components/menu/MenuBackButton.vue';

    @Component({
        components: { DropdownButton, DropdownItem, MenuBackButton },
    })
    export default class FilterOptionsMenu extends Mixins(TranslationMixin) {
        @Prop({ required: true, type: Array }) public options!: Array<Option>;
        @Prop({ required: true, type: Array }) public appliedValues!: Array<number>;
        @Prop({ required: true, type: [String, Number] }) public filterType!: FilterType;

        public selectedValues: Array<number | string> = [];

        public searchQuery = '';

        public get searchFieldPlaceholder() {
            return this.trans('spa.admin.course-organization.filters-menu.search') + '...';
        }

        public get menuLabel() {
            const total = this.options.length;
            const selectedCount = this.selectedValues.length;

            const variables = { num: selectedCount > 0 ? `${selectedCount}/${total}` : total };

            return this.trans(
                `spa.admin.course-organization.filters-menu.options.selected-label.${this.filterType}`,
                variables
            );
        }

        @Watch('appliedValues', { immediate: true })
        public updateSelectedValues() {
            this.selectedValues = [...this.appliedValues];
        }

        @Emit('back')
        public handleBackButtonClick() {
            return undefined;
        }

        public get areAllOptionsSelected() {
            return this.options.length === this.selectedValues.length;
        }

        public get canSearch() {
            return this.filterType !== FilterType.CourseType;
        }

        public get filteredOptions(): Array<Option> {
            return this.options.filter(option =>
                option.label.toLowerCase().includes(this.searchQuery.trim().toLowerCase())
            );
        }

        public handleOptionClick(option: Option) {
            const optionIndex = this.selectedValues.indexOf(option.value);
            const isFound = optionIndex !== -1;

            if (isFound) {
                this.selectedValues.splice(optionIndex, 1);
            } else {
                this.selectedValues.push(option.value);
            }
        }

        public handleSelectAll() {
            if (this.areAllOptionsSelected) {
                this.selectedValues = [];
            } else {
                this.selectedValues = this.options.map(option => option.value);
            }
        }

        @Emit('confirm')
        public handleConfirm() {
            return [...this.selectedValues];
        }
    }
</script>

<style scoped lang="less">
    .options-menu {
        display: flex;
        flex-direction: column;
        width: 320px;
        max-height: 100%;
    }

    .menu-title {
        .flex-center-content-vertically();

        padding: @spacing-20 @spacing-24 @spacing-14 @spacing-24;
    }

    .select-all-option {
        flex-shrink: 0;
        margin-top: @spacing-4;
    }

    .divider {
        height: 1px;
        margin-top: @spacing-4;
        background-color: @info-color-8;
    }

    .options-list {
        @list-item-height: 48px;

        max-height: calc(@list-item-height * 3);
        margin: 0;
        margin-top: @spacing-4;
        padding: 0;
        overflow: auto;
        list-style: none;

        @media screen and (min-height: 750px) {
            max-height: calc(@list-item-height * 5);
        }

        @media screen and (min-height: 900px) {
            max-height: calc(@list-item-height * 7);
        }
    }

    .menu-footer {
        display: flex;
        justify-content: flex-end;
        padding: @spacing-16;
    }
</style>
